import { ImSys } from '../core/ImSys.js';
import { Widget } from '../core/Widget.js';
import { ViewWidget } from '../view/ViewWidget.js';

/**
 * Виджет радиогруппы (hidden[data-ext="radio"] + input[type=radio][name=data-key]).
 * Инкапсулирует:
 *  - связку id/for для кнопок и label;
 *  - синхронизацию checked → hidden.value;
 *  - API WithMainValue (get/set/has).
 */
class RadioboxWidget extends Widget {
    constructor() {
        super(...arguments);
        this._unsubs = [];
    }
    _radios() {
        const parentView = this.element.closest('.im-view') ?? document;
        // Получаем радио боксы в пределах view
        return parentView.querySelectorAll(`input[type="radio"][name="${this.dataKey}"]${this.notUIMask}`);
    }
    /**
    * Инициализация виджета
    * @param op Опции виджета
    */
    _create(op) {
        this.dataKey = this.element.dataset.key;
        this.notUIMask = ViewWidget.closestView(this.element)?.getUiNotMask() || '';
        const radios = this._radios();
        radios.forEach(r => {
            // id/for для label сразу после input
            if (!r.id) {
                const id = '_id' + ImSys.tmp_id++;
                r.id = id;
                const lab = r.nextElementSibling;
                if (lab && lab.tagName.toLowerCase() === 'label' && !lab.htmlFor) {
                    lab.htmlFor = id;
                }
            }
            const onChange = () => {
                if (r.checked) {
                    // важно: пустое значение остаётся пустой строкой (allowNull)
                    this.element.value = r.value;
                    // синхронизируем наружу
                    this.element.dispatchEvent(new Event('change', { bubbles: true }));
                }
            };
            r.addEventListener('change', onChange);
            this._unsubs.push(() => r.removeEventListener('change', onChange));
        });
        // ⬇️ ИНИЦИАЛИЗАЦИЯ ИЗ HIDDEN (носитель данных)
        let want = this.element.value; // текущее значение hidden (приоритет)
        radios.forEach(r => {
            const should = r.value === want;
            r.checked = should; // приводим radios к hidden
        });
    }
    /** Вернёт выбранное значение ('' допустимо), иначе null */
    getValue() {
        const radios = this._radios();
        for (const r of Array.from(radios)) {
            if (r.checked)
                return r.value; // '' — валидно
        }
        return null; // нет выбранной → в encodeValues станет "dbnull"
    }
    /** Установит выбранную кнопку по значению ('' поддерживается) */
    setValue(val) {
        const want = val ?? '';
        let matched = false;
        this._radios().forEach(r => {
            if (r.value === want) {
                r.checked = true;
                matched = true;
            }
            else {
                r.checked = false;
            }
        });
        this.element.value = matched ? want : '';
        return true;
    }
    /** Есть ли выбранная радиокнопка */
    hasValue() {
        const radios = this._radios();
        for (const r of Array.from(radios))
            if (r.checked)
                return true;
        return false;
    }
    /** Получение названия значения исходя из разметки или контекста */
    getValueTitle() {
        const value = this.getValue();
        if (value == null)
            return null;
        const radios = this._radios();
        for (const r of Array.from(radios)) {
            if (r.checked) {
                const id = r.id;
                if (!id)
                    return null;
                const label = r
                    .closest('label')
                    ?? r.parentElement?.querySelector(`label[for="${CSS.escape(id)}"]`);
                const text = label?.textContent?.trim();
                return text || null;
            }
        }
        return null;
    }
    _onDestroy() {
        // снять все слушатели
        this._unsubs.splice(0).forEach(off => off());
    }
}
/** Имя виджета */
RadioboxWidget._alias = "Radiobox";

export { RadioboxWidget };
